import React from 'react';
import {
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Chip,
  List,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

/**
 * GroupedAccordionList - Display grouped data in an accordion grid layout
 *
 * @param {Object} props
 * @param {Array} props.groups - Array of group objects from useGroupedData
 * @param {string} props.itemsKey - Key name for items array in each group (default: 'items')
 * @param {Function} props.isGroupExpanded - Function to check if group is expanded
 * @param {Function} props.onToggleGroup - Callback when group is toggled
 * @param {Function} props.renderItem - Function to render each item in the list
 * @param {Function} props.renderGroupHeader - Optional custom group header renderer
 * @param {string} props.groupNameKey - Key for group name (default: 'examName')
 * @param {string} props.groupDateKey - Key for group date (default: 'examDate')
 * @param {string} props.itemLabel - Label for items count (default: 'student')
 * @param {React.ReactNode} props.groupIcon - Icon for group header (default: DescriptionIcon)
 * @param {boolean} props.loading - Show loading state
 * @param {React.ReactNode} props.emptyState - Content to show when no groups
 * @param {Object} props.gridProps - Props to pass to the Grid container
 */
const GroupedAccordionList = ({
  groups = [],
  itemsKey = 'items',
  isGroupExpanded,
  onToggleGroup,
  renderItem,
  renderGroupHeader,
  groupNameKey = 'examName',
  groupDateKey = 'examDate',
  itemLabel = 'student',
  groupIcon,
  loading = false,
  emptyState,
  gridProps = {},
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (groups.length === 0) {
    return (
      emptyState || (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: 'text.secondary',
          }}
        >
          <Typography variant="h6">No data found</Typography>
        </Box>
      )
    );
  }

  const GroupIcon = groupIcon || DescriptionIcon;

  return (
    <Grid container spacing={3} {...gridProps}>
      {groups.map((group) => {
        const groupId = group.groupId;
        const items = group[itemsKey] || [];
        const expanded = isGroupExpanded ? isGroupExpanded(groupId) : false;
        const itemCount = items.length;
        const itemLabelPlural = itemCount !== 1 ? `${itemLabel}s` : itemLabel;

        return (
          <Grid item xs={12} md={6} lg={4} key={groupId}>
            <Accordion
              expanded={expanded}
              onChange={() => onToggleGroup && onToggleGroup(groupId)}
              disableGutters
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 72,
                  px: 3,
                  '& .MuiAccordionSummary-content': {
                    my: 2,
                  },
                }}
              >
                {renderGroupHeader ? (
                  renderGroupHeader(group)
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      pr: 2,
                    }}
                  >
                    <GroupIcon
                      sx={{ mr: 2, color: 'primary.main', fontSize: 28 }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          mb: 0.5,
                        }}
                      >
                        {group[groupNameKey] || 'Unknown'}
                      </Typography>
                      {group[groupDateKey] && (
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {format(
                            new Date(group[groupDateKey]),
                            'dd-MM-yyyy HH:mm'
                          )}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={`${itemCount} ${itemLabelPlural}`}
                      size="small"
                      sx={{
                        mr: 2,
                        fontWeight: 500,
                        backgroundColor: 'primary.50',
                        color: 'primary.main',
                      }}
                    />
                  </Box>
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, backgroundColor: 'grey.25' }}>
                <List sx={{ py: 0 }}>
                  {items.map((item, index) =>
                    renderItem ? renderItem(item, index, group) : null
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default GroupedAccordionList;
